<script lang="ts">
	// Compact, always-visible usage-limit meter for the sidebar footer (admins
	// only — the parent gates on me()?.isAdmin). A miniature of the Providers
	// tab's "Usage & limits" panels: one thin bar per limit window (Claude per
	// Max account: 5h + weekly; OpenAI subscription: 5h + weekly). Click-through
	// goes to the full widget at /marketplace/admin?tab=providers.
	import { onMount, onDestroy } from 'svelte';
	import {
		getClaudeUsage,
		getProviderUsage,
		type ClaudeUsage,
		type OpenAiUsage,
		type RateWindow,
	} from '$lib/provider-config-api';
	import GaugeIcon from '@lucide/svelte/icons/gauge';

	// key: unique {#each} key derived from the account's stable id — labels can
	// collide (two same-domain Max accounts) and a duplicate key crashes the render.
	type Row = { key: string; label: string; pct: number };

	let claude = $state<ClaudeUsage>(null);
	let openai = $state<OpenAiUsage>(null);
	let loaded = $state(false);

	async function load(): Promise<void> {
		// Both feeds are server-cached (≤300s), so polling them is cheap. Errors
		// are non-fatal — the meter just omits the unavailable provider.
		const [c, o] = await Promise.allSettled([getClaudeUsage(), getProviderUsage()]);
		claude = c.status === 'fulfilled' ? c.value : null;
		openai = o.status === 'fulfilled' ? o.value : null;
		loaded = true;
	}

	let timer: ReturnType<typeof setInterval> | null = null;
	onMount(() => {
		void load();
		timer = setInterval(() => void load(), 5 * 60_000);
	});
	onDestroy(() => {
		if (timer) clearInterval(timer);
	});

	function winLabel(minutes: number): string {
		if (minutes === 300) return '5h';
		if (minutes === 10080) return 'wk';
		const h = Math.round(minutes / 60);
		return h >= 24 ? `${Math.round(h / 24)}d` : `${h}h`;
	}

	const rows = $derived.by<Row[]>(() => {
		const out: Row[] = [];
		for (const [i, acc] of (claude?.accounts ?? []).entries()) {
			const k = acc.id ?? `acct-${i}`;
			if (acc.fiveHour) out.push({ key: `${k}-5h`, label: `${acc.label} 5h`, pct: acc.fiveHour.utilization });
			if (acc.weekly) out.push({ key: `${k}-wk`, label: `${acc.label} wk`, pct: acc.weekly.utilization });
			// Per-model weekly caps (e.g. "blocher Fable"). The whole point of this meter is
			// the BINDING limit, and the scoped cap usually binds long before the aggregate
			// weekly one does — an account can read 64% wk while Fable is at 98%.
			for (const s of acc.scoped ?? [])
				out.push({ key: `${k}-sc-${s.key}`, label: `${acc.label} ${s.label}`, pct: s.utilization });
		}
		const ow = openai ? [openai.primary, openai.secondary].filter((w): w is RateWindow => w !== null) : [];
		for (const [j, w] of ow.entries()) out.push({ key: `gpt-${j}`, label: `GPT ${winLabel(w.windowMinutes)}`, pct: w.usedPercent });
		return out;
	});

	function barColor(pct: number): string {
		if (pct >= 90) return 'bg-destructive';
		if (pct >= 70) return 'bg-amber-500';
		return 'bg-primary';
	}
</script>

{#if loaded && rows.length > 0}
	<a
		href="/marketplace/admin?tab=providers"
		class="flex flex-col gap-1 rounded-md px-2.5 py-1.5 transition-colors hover:bg-sidebar-accent"
		title="Usage limits — open Providers settings"
	>
		<span class="flex items-center gap-2 text-xs font-medium text-sidebar-foreground">
			<GaugeIcon class="size-3.5" />
			Usage
		</span>
		<div class="flex flex-col gap-[3px]">
			{#each rows as row (row.key)}
				<div class="flex items-center gap-1.5">
					<span class="w-[72px] shrink-0 truncate text-[10px] leading-tight text-[color:var(--fg-faint)]" title={row.label}>
						{row.label}
					</span>
					<div class="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-sidebar-border">
						<div
							class={`h-full rounded-full ${barColor(row.pct)}`}
							style={`width: ${Math.min(100, Math.max(0, row.pct))}%`}
						></div>
					</div>
					<span class="w-7 shrink-0 text-right text-[10px] tabular-nums leading-tight text-[color:var(--fg-faint)]">
						{Math.round(row.pct)}%
					</span>
				</div>
			{/each}
		</div>
	</a>
{/if}
